import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  CalendarDays,
  type LucideIcon,
  Megaphone,
  Package,
  ShoppingCart,
  Truck,
  Wallet,
} from 'lucide-react';
import { features, hasFeature, type Feature } from '@/config/features';

interface Highlight {
  icon: LucideIcon;
  titleKey: string;
  bodyKey: string;
  /** Omitted entries always show; gated ones follow the client's configuration. */
  feature?: Feature;
}

const HIGHLIGHTS: Highlight[] = [
  {
    icon: ShoppingCart,
    titleKey: 'auth.brand.orders',
    bodyKey: 'auth.brand.ordersBody',
  },
  {
    icon: CalendarDays,
    titleKey: 'auth.brand.bookings',
    bodyKey: 'auth.brand.bookingsBody',
    feature: 'booking',
  },
  {
    icon: Package,
    titleKey: 'auth.brand.catalog',
    bodyKey: 'auth.brand.catalogBody',
  },
  {
    icon: Truck,
    titleKey: 'auth.brand.delivery',
    bodyKey: 'auth.brand.deliveryBody',
  },
  {
    icon: Megaphone,
    titleKey: 'auth.brand.marketing',
    bodyKey: 'auth.brand.marketingBody',
  },
  {
    icon: Wallet,
    titleKey: 'auth.brand.finance',
    bodyKey: 'auth.brand.financeBody',
  },
];

/**
 * What the portal covers, for this client — a client with no booking service
 * configured never sees bookings advertised, the same rule the sidebar follows.
 *
 * Two presentations, one DOM node. Above `lg` it is the right-hand column of the
 * auth grid. Below `lg` the grid collapses to one column and this simply falls
 * *below* the form: still readable, just past the fold, with the decoration
 * dialled back so it reads as a footer rather than a second hero. It is not
 * hidden — the support line at the bottom is the one thing a person locked out
 * on a phone actually needs.
 */
export function LoginBrandPanel() {
  const { t } = useTranslation();
  const highlights = HIGHLIGHTS.filter((item) => hasFeature(item.feature));

  return (
    <div className="bg-muted relative overflow-hidden border-t lg:border-t-0">
      {/* Two offset washes rather than one flat gradient, so the panel has some
          depth behind the content without needing an image asset. */}
      <div
        aria-hidden
        className="from-primary/20 via-primary/5 absolute inset-0 bg-gradient-to-br to-transparent"
      />
      <div
        aria-hidden
        className="bg-primary/10 absolute -top-24 -right-16 size-64 rounded-full blur-3xl lg:-top-32 lg:-right-24 lg:size-96"
      />
      <div
        aria-hidden
        className="bg-primary/8 absolute -bottom-24 -left-16 size-72 rounded-full blur-3xl lg:-bottom-40 lg:-left-20 lg:size-[28rem]"
      />

      <div className="relative flex h-full flex-col justify-between gap-8 px-6 py-10 sm:px-10 lg:gap-10 lg:p-12 xl:p-16">
        <div className="max-w-md space-y-3">
          <p className="text-primary text-xs font-semibold tracking-[0.18em] uppercase">
            {t('auth.brand.eyebrow')}
          </p>
          <h2 className="text-xl leading-tight font-semibold tracking-tight text-balance sm:text-2xl lg:text-3xl xl:text-4xl">
            {t('auth.brand.headline')}
          </h2>
          <p className="text-muted-foreground hidden text-sm text-pretty sm:block">
            {t('auth.brand.subhead')}
          </p>
        </div>

        <ul className="grid max-w-lg grid-cols-2 gap-x-6 gap-y-5 lg:gap-x-8">
          {highlights.map((item) => (
            <li key={item.titleKey} className="flex gap-3">
              <span className="bg-primary/12 text-primary flex size-8 shrink-0 items-center justify-center rounded-lg">
                <item.icon className="size-4" />
              </span>
              <div className="space-y-0.5">
                <p className="text-sm font-medium">{t(item.titleKey)}</p>
                <p className="text-muted-foreground hidden text-xs leading-relaxed text-pretty sm:block">
                  {t(item.bodyKey)}
                </p>
              </div>
            </li>
          ))}
        </ul>

        <div className="max-w-md space-y-4">
          <div className="border-primary/20 bg-background/45 rounded-xl border p-4 backdrop-blur-sm">
            <div className="text-primary mb-2 flex items-center gap-2">
              <BarChart3 className="size-4" />
              <span className="text-xs font-semibold tracking-wider uppercase">
                {t(
                  features.booking
                    ? 'auth.brand.calloutWithBooking'
                    : 'auth.brand.calloutWithoutBooking'
                )}
              </span>
            </div>
            <p className="text-muted-foreground text-xs leading-relaxed text-pretty">
              {t(
                features.booking
                  ? 'auth.brand.calloutBodyWithBooking'
                  : 'auth.brand.calloutBodyWithoutBooking'
              )}
            </p>
          </div>

          <p className="text-muted-foreground/70 text-xs">
            {t('auth.brand.support')}
          </p>
        </div>
      </div>
    </div>
  );
}
